import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ProfileModal({ open, onClose }) {
    const { user, setUser } = useAuth();
    const [fullName, setFullName] = useState("");
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open && user) {
            setFullName(user.full_name || "");
            setTimeout(() => inputRef.current?.focus(), 60);
        }
    }, [open, user]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/auth/me", {
                full_name: fullName.trim() || null,
            });
            setUser(data);
            toast.success("Profile updated", { duration: 1500 });
            onClose();
        } catch (err) {
            toast.error(formatApiError(err), { duration: 2500 });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div
                className="sheet-backdrop"
                onClick={onClose}
                aria-hidden
                data-testid="profile-modal-backdrop"
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Your profile"
                data-testid="profile-modal"
                className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-[440px] -translate-x-1/2 -translate-y-1/2 glass-card !p-0"
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--divider)]">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-400">
                            Account
                        </div>
                        <h3 className="text-[18px] font-semibold text-slate-900 mt-0.5">
                            Your profile
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-secondary btn-sm btn-icon"
                        aria-label="Close"
                        data-testid="profile-modal-close"
                    >
                        <X size={16} strokeWidth={2} />
                    </button>
                </div>
                <div className="px-5 py-5">
                    <div>
                        <label className="label" htmlFor="profile-fullname">
                            Full name
                        </label>
                        <input
                            id="profile-fullname"
                            ref={inputRef}
                            className="input"
                            placeholder="e.g., Jane Doe"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") save();
                            }}
                            autoCapitalize="words"
                            data-testid="profile-fullname-input"
                        />
                        <div className="hint mt-1">
                            Shown on your reports and in team lists.
                        </div>
                    </div>
                    <div className="mt-3">
                        <div className="label">Email</div>
                        <div className="input input-locked" data-testid="profile-email-locked">
                            {user?.email || "—"}
                        </div>
                    </div>
                </div>
                <div className="px-5 py-4 border-t border-[var(--divider)] flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onClose}
                        data-testid="profile-modal-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={save}
                        disabled={saving}
                        data-testid="profile-modal-save"
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </>
    );
}
